import { useState, useEffect, useRef } from 'react';

import {
  formatError
} from 'app/utils/valueFormatters';

const isNewRow = row => {
  return !!row.id.toString().match(/^new-/);
}

const useChanges = () => {
  const initialState = { byId: {} };
  const [changes, setChanges] = useState(initialState);

  const setChange = (id, field, value) => {
    let change = { ...(changes.byId[id] || {}) };
    change.id = id;
    change[field] = value;

    let newChanges = { ...changes };
    newChanges.byId[id] = change;

    setChanges(newChanges);
  }

  const clearChange = id => {
    let newChanges = { ...changes };
    delete newChanges.byId[id];

    setChanges(newChanges);
  }

  const clearChanges = () => setChanges(initialState);

  return {
    changes,
    setChange,
    clearChange,
    clearChanges
  }
}

const useErrors = () => {
  const initialState = { byId: {} };
  const [errors, setErrors] = useState(initialState);

  const setError = (id, error) => {
    let newErrors = { ...errors };
    newErrors.byId[id] = formatError(error);

    setErrors(newErrors);
  }

  const clearError = id => {
    let newErrors = { ...errors };
    delete newErrors.byId[id];

    setErrors(newErrors);
  }

  const clearErrors = () => setErrors(initialState);

  return {
    errors,
    setError,
    clearError,
    clearErrors
  }
}

const useQuickEdit = props => {
  const {
    quickEditEnabled,
    onQuickAdd,
    onQuickEdit,
    newRowTemplate
  } = props;

  const onActiveRef = useRef(null);
  const onActive = callback => {
    onActiveRef.current = callback;
  }

  const onInactiveRef = useRef(null);
  const onInactive = callback => {
    onInactiveRef.current = callback;
  }

  const onRowUpdateRef = useRef(null);
  const onRowUpdate = callback => {
    onRowUpdateRef.current = callback;
  }

  const [active, setActive] = useState(false);
  const { changes, setChange, clearChange, clearChanges } = useChanges();
  const { errors, setError, clearError, clearErrors } = useErrors();

  // keep one quick edit select open at a time.
  // the input's id will be stored as the currentInput
  const [currentInput, setCurrentInput] = useState(null);

  // set flag to prevent re-opening select immediately after closing it.
  // openSelect will be immediatey called by onFocus after choosing an option
  let closingCurrentInput = false;

  const openSelect = id => {
    if (!closingCurrentInput) {
      setCurrentInput(id);
    }
  }

  const closeSelect = () => {
    closingCurrentInput = true;
    setCurrentInput(null);

    setTimeout(() => {
      closingCurrentInput = false;
    }, 500);
  }

  const isSelectOpen = id => {
    return id === currentInput;
  }

  const activate = () => {
    setActive(true);
    onActiveRef.current();
  }

  const deactivate = () => {
    setActive(false);
    onInactiveRef.current();
  }

  const onAdd = () => {

  }

  const commitChanges = async () => {
    // get rows to create or update from changes
    let rowsToUpdate = Object.keys(changes.byId)
      .map(k => changes.byId[k]);

    for (let row of rowsToUpdate) {
      let params = {...row};

      try {
        let updatedRow;
        if (isNewRow(row)) {
          // delete fake ID before posting
          delete params.id;
          updatedRow = await onQuickAdd(params);
        } else {
          updatedRow = await onQuickEdit(row);
        }

        clearChange(row.id);
        clearError(row.id);
        onRowUpdateRef.current(updatedRow);
        setActive(false);

      } catch(err) {
        console.error(err);
        setError(row.id, err);

        throw 'There was an error saving the data. Please check the rows highlighted in red.';
      }
    }
  }

  const discardChanges = () => {
    clearChanges();
    deactivate();
  }

  return {
    enabled: quickEditEnabled,
    active,
    activate,
    deactivate,
    openSelect,
    closeSelect,
    isSelectOpen,
    changes,
    setChange,
    onAdd,
    commitChanges,
    discardChanges,
    errors,
    clearError,
    clearErrors,
    onActive,
    onInactive,
    onRowUpdate
  }
}

export default useQuickEdit;
